// src/views/admin/kitchenScreen/KitchenScreen.jsx
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  getKitchenOrdersService,
} from '@/services/OrderServices';                    // ← unchanged
import { getDashboardService } from '@/services/DashboardServices'; // ← NEW

import MostPopularItemsComponent
  from '@/views/admin/dashboard/component/MostPopularItemsComponent';

/* ╭── debug flag ─────────────────────────────────────────────╮ */
const DEBUG = true;
const log = (...a) => DEBUG && console.log('[KitchenScreen]', ...a);
/* ╰────────────────────────────────────────────────────────────╯ */

export default function KitchenScreen() {
  const dispatch = useDispatch();

  /* ───────── state ───────── */
  const [orders, setOrders]         = useState([]);
  const [popularData, setPopularData] = useState({ popular_items: [] });
  const [fullScreen, setFullScreen] = useState(false);

  /* ───────── lifecycle ───────── */
  useEffect(() => {
    fetchOrders();
    fetchPopular();
    const timer = setInterval(fetchOrders, 5_000);
    return () => clearInterval(timer);
  }, []);

  /* ───────── orders ───────── */
  async function fetchOrders() {
    try {
      const { data: resp } = await dispatch(
        getKitchenOrdersService({ statuses: [4, 6] })
      );
      const list = resp?.data?.data ?? resp?.data ?? [];
      log('orders length →', list.length, list.slice(0, 3));
      setOrders(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error('[KitchenScreen] orders error', err);
      setOrders([]);
    }
  }

  /* ───────── popular items via dashboard service ───────── */
  async function fetchPopular() {
    try {
      const { data: resp } = await dispatch(getDashboardService());

      log('popular RAW resp →', resp);

      const items =
        resp?.data?.data?.popular_items   // { data: { data: { popular_items } } }
        ?? resp?.data?.popular_items      // { data: { popular_items } }
        ?? resp?.popular_items            // { popular_items }
        ?? [];

      log('popular extracted list → length:', items.length, items);

      setPopularData({ popular_items: Array.isArray(items) ? items : [] });
    } catch (err) {
      console.error('[KitchenScreen] popular error', err);
      setPopularData({ popular_items: [] });
    }
  }

  /* ───────── buckets ───────── */
  const preparing = orders.filter(o => o.status === 4);
  const ready     = orders.filter(o => o.status === 6);

  /* ───────── helpers ───────── */
  const fmtId = (raw, n = 6) =>
    raw ? String(raw).slice(-n).padStart(n, '0') : '-'.repeat(n);

  const wrapperCls = fullScreen
    ? 'fixed inset-0 z-50 flex flex-col bg-gray-100'
    : 'flex flex-col h-full bg-gray-100';

  /* ───────── render ───────── */
  return (
    <div className={wrapperCls}>
      {/* header */}
      <div className="flex items-center justify-between px-6 py-4 bg-white shadow-md z-10">
        <h1 className="text-2xl font-semibold text-gray-800">
          Kitchen Dashboard
        </h1>

        <button
          onClick={() => setFullScreen(f => !f)}
          className="p-2 bg-gray-100 rounded-md hover:bg-gray-200 transition"
          title={fullScreen ? 'Exit full screen' : 'Full screen'}
        >
          <i className={`fas ${fullScreen ? 'fa-compress' : 'fa-expand'}`} />
        </button>
      </div>

      {/* body */}
      <div className="flex-1 p-6 flex gap-6 overflow-auto">
        {/* popular items */}
        <div className="w-[45%] max-w-[640px]">
          <MostPopularItemsComponent dataSource={popularData} />
        </div>

        {/* orders */}
        <div className="flex-1 grid grid-cols-2 gap-4">
          <OrderColumn title="Preparing"       color="blue"
                       orders={preparing}      formatId={fmtId} />
          <OrderColumn title="Out for Delivery" color="green"
                       orders={ready}          formatId={fmtId} />
        </div>
      </div>
    </div>
  );
}

/* ────────────────── OrderColumn ────────────────── */
function OrderColumn({ title, color, orders, formatId }) {
  const headClr = color === 'blue' ? 'bg-[#0A66FF]' : 'bg-[#12B76A]';

  return (
    <div className="bg-white rounded-2xl shadow-lg flex flex-col">
      <div className={`${headClr} rounded-t-2xl py-4`}>
        <h3 className="text-center text-white text-xl font-semibold">
          {title}
        </h3>
      </div>

      <div className="flex-1 flex flex-col items-center py-10 space-y-12">
        {orders.length ? (
          orders.map(o => (
            <div key={o._id || o.id} className="flex flex-col items-center">
              <span className="text-[56px] leading-none font-extrabold text-[#101828]">
                {formatId(o.order_serial_no ?? o.id)}
              </span>

              {o.dining_table_id?.name ? (
                <span className="mt-1 text-sm text-gray-500">
                  Table&nbsp;{o.dining_table_id.name}
                </span>
              ) : o.token ? (
                <span className="mt-1 text-sm text-gray-500">
                  #{o.token}
                </span>
              ) : null}
            </div>
          ))
        ) : (
          <p className="text-gray-400">No orders</p>
        )}
      </div>
    </div>
  );
}
